import { motion } from 'framer-motion';
import React from 'react';
import { cn } from '../../../lib/utils';

interface NpsData {
  npsScore: number;
  totalResponses: number;
  promoters: number;
  passives: number;
  detractors: number;
  promotersPercentage: number;
  passivesPercentage: number;
  detractorsPercentage: number;
}

interface NpsChartProps {
  data: NpsData;
  title: string;
  color?: string;
}

const NpsChart: React.FC<NpsChartProps> = ({ data }) => {
  const {
    npsScore,
    totalResponses,
    promoters,
    passives,
    detractors,
    promotersPercentage,
    passivesPercentage,
    detractorsPercentage,
  } = data;

  // Determine NPS score color based on value
  const getNpsScoreColor = (score: number) => {
    if (score >= 50) return 'text-green-600';
    if (score >= 0) return 'text-yellow-600';

    return 'text-red-600';
  };

  const getNpsScoreBgColor = (score: number) => {
    if (score >= 50) {
      return 'bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20';
    }

    if (score >= 0) {
      return 'bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20';
    }

    return 'bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20';
  };

  return (
    <div className="w-full">
      <div className="flex items-center gap-8">
        {/* NPS Score Header - Left Side */}
        <motion.div
          className="flex-shrink-0"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <div className="text-center">
            <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2 uppercase tracking-wide">
              NPS Score
            </div>
            <motion.div
              className={cn(
                'inline-flex items-center justify-center w-14 h-14 rounded-xl text-xl font-bold shadow-lg',
                'ring-4 ring-white dark:ring-gray-800 transform transition-all duration-300',
                'hover:scale-105 hover:shadow-xl cursor-default',
                getNpsScoreBgColor(npsScore),
                getNpsScoreColor(npsScore)
              )}
              whileHover={{ scale: 1.05, y: -2, transition: { duration: 0.2 } }}
              whileTap={{ scale: 0.98 }}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2, type: 'spring', stiffness: 200 }}
            >
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.5 }}
              >
                {npsScore}
              </motion.span>
            </motion.div>
            <motion.div
              className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.6 }}
            >
              {totalResponses.toLocaleString()} response{totalResponses !== 1 ? 's' : ''}
              {totalResponses < 30 && totalResponses > 0 && (
                <div className="text-xs text-amber-500 dark:text-amber-400 mt-1">
                  <span role="img" aria-label="warning">
                    ⚠️
                  </span>{' '}
                  Small sample size
                </div>
              )}
            </motion.div>
          </div>
        </motion.div>

        {/* Enhanced NPS Breakdown Bars - Right Side */}
        <div className="flex-1 space-y-3">
          {/* Promoters (9-10) */}
          <motion.div
            className="group"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-green-400 to-green-600 shadow-sm"></div>
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Promoters
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">(9-10)</div>
              </div>
              <div className="text-sm font-bold text-gray-800 dark:text-gray-200">
                {promoters.toLocaleString()}
              </div>
            </div>
            <div className="relative bg-gray-100 dark:bg-gray-700 rounded-lg h-6 overflow-hidden shadow-inner group-hover:shadow-md transition-shadow duration-300">
              <motion.div
                className="h-full rounded-lg bg-gradient-to-r from-green-400 to-green-600 shadow-sm relative overflow-hidden"
                initial={{ width: 0 }}
                animate={{ width: `${promotersPercentage}%` }}
                transition={{ duration: 1, delay: 0.8, ease: 'easeOut' }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-50"></div>
              </motion.div>
              <motion.div
                className="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-700 dark:text-gray-300"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 1.2 }}
              >
                {promotersPercentage}%
              </motion.div>
            </div>
          </motion.div>

          {/* Passives (7-8) */}
          <motion.div
            className="group"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.9 }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-600 shadow-sm"></div>
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Passives
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">(7-8)</div>
              </div>
              <div className="text-sm font-bold text-gray-800 dark:text-gray-200">
                {passives.toLocaleString()}
              </div>
            </div>
            <div className="relative bg-gray-100 dark:bg-gray-700 rounded-lg h-6 overflow-hidden shadow-inner group-hover:shadow-md transition-shadow duration-300">
              <motion.div
                className="h-full rounded-lg bg-gradient-to-r from-yellow-400 to-yellow-600 shadow-sm relative overflow-hidden"
                initial={{ width: 0 }}
                animate={{ width: `${passivesPercentage}%` }}
                transition={{ duration: 1, delay: 1.0, ease: 'easeOut' }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-50"></div>
              </motion.div>
              <motion.div
                className="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-700 dark:text-gray-300"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 1.4 }}
              >
                {passivesPercentage}%
              </motion.div>
            </div>
          </motion.div>

          {/* Detractors (0-6) */}
          <motion.div
            className="group"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 1.1 }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-red-400 to-red-600 shadow-sm"></div>
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Detractors
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">(0-6)</div>
              </div>
              <div className="text-sm font-bold text-gray-800 dark:text-gray-200">
                {detractors.toLocaleString()}
              </div>
            </div>
            <div className="relative bg-gray-100 dark:bg-gray-700 rounded-lg h-6 overflow-hidden shadow-inner group-hover:shadow-md transition-shadow duration-300">
              <motion.div
                className="h-full rounded-lg bg-gradient-to-r from-red-400 to-red-600 shadow-sm relative overflow-hidden"
                initial={{ width: 0 }}
                animate={{ width: `${detractorsPercentage}%` }}
                transition={{ duration: 1, delay: 1.2, ease: 'easeOut' }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-50"></div>
              </motion.div>
              <motion.div
                className="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-700 dark:text-gray-300"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 1.6 }}
              >
                {detractorsPercentage}%
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Compact Score Range Legend */}
      <motion.div
        className="mt-3 flex justify-center gap-6 text-xs"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 1.8 }}
      >
        <div className="flex items-center space-x-1">
          <div className="w-2 h-2 rounded-full bg-gradient-to-r from-green-400 to-green-600"></div>
          <span className="text-gray-600 dark:text-gray-400 font-medium">Promoters (9-10)</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-2 h-2 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-600"></div>
          <span className="text-gray-600 dark:text-gray-400 font-medium">Passives (7-8)</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-2 h-2 rounded-full bg-gradient-to-r from-red-400 to-red-600"></div>
          <span className="text-gray-600 dark:text-gray-400 font-medium">Detractors (0-6)</span>
        </div>
      </motion.div>
    </div>
  );
};

export default NpsChart;
